import styled, { css } from 'styled-components';
import { getInvertedColor, useWindowWidth } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import { createPortal } from 'react-dom';
import { StyledFreeLink, useTheme } from '../../../common';
import { usePlayer } from '../../PlayerProvider';
import { Playlist, PlaylistItemComponent } from '../../playlist';
import { Bar } from '../widget-types';
import { useFloating } from './FloatingProvider';

export function FloatingBar() {
  const width = useWindowWidth();
  const { font, displayDeactivation } = useSettings();
  const { textColor, backgroundStyle } = useTheme();
  const { height } = useFloating();
  const { showPlayer } = usePlayer();

  return createPortal(
    <>
      <StyledPlaylist
        maxHeight="100%"
        hideDirection="toRight"
        _font={font?.value || 'inherit'}
        _playerHeight={height}
        _windowWidth={width}
        _backgroundStyle={backgroundStyle}
        _color={textColor}
        _displayDeactivation={displayDeactivation}
      />

      <Container _font={font?.value || 'inherit'}>
        <Inner>
          <StyledBar _backgroundStyle={backgroundStyle} _color={textColor} />
        </Inner>

        {showPlayer && <StyledFreeLink />}
      </Container>
    </>,
    document.body
  );
}

const StyledPlaylist = styled(Playlist)<{
  _playerHeight: number;
  _windowWidth: number;
  _color: string;
  _font: string;
  _backgroundStyle?: string;
  _displayDeactivation?: boolean;
}>`
  position: fixed;
  right: 0;
  padding: 0 6px;
  max-width: 100%;
  box-sizing: border-box;
  margin: 0;
  box-shadow: 0 0 10px 0 rgba(17, 17, 17, 0.2);
  z-index: 999999999;
  bottom: ${({ _playerHeight }) => _playerHeight}px;
  height: ${({ _playerHeight }) => `calc(100% - ${_playerHeight}px)`};
  width: ${({ _windowWidth }) =>
    _windowWidth <= 348 && _windowWidth ? '100%' : '296px'};
  color: ${({ _color }) => _color};
  font-family: ${({ _font }) => _font};

  ${({ _displayDeactivation }) =>
    _displayDeactivation &&
    css`
      opacity: 0.3;
      pointer-events: none;
    `}

  ${({ _backgroundStyle }) => _backgroundStyle};

  & ${PlaylistItemComponent} {
    &:first-child {
      margin-top: 6px;
    }

    &:last-child {
      margin-bottom: 12px;
    }
  }
`;

const StyledBar = styled(Bar)<{
  _color: string;
  _backgroundStyle?: string;
}>`
  color: ${({ _color }) => getInvertedColor(_color)};

  ${({ _backgroundStyle }) => _backgroundStyle}
`;

const Container = styled.div<{
  _font: string;
}>`
  z-index: 999999999;
  width: 100%;
  position: fixed;
  display: flex;
  flex-direction: column;
  bottom: 0;
  left: 0;
  font-family: ${({ _font }) => _font};
`;

const Inner = styled.div`
  width: 100%;
  position: relative;
  height: auto;
`;
